import {
  Graph,
  layout,
  type EdgeLabel,
  type GraphLabel,
  type NodeLabel,
  type Point,
} from "@dagrejs/dagre";
import clsx from "clsx";
import { Bot, CircleStop, Plus, Radio, Timer } from "lucide-react";

import type { CircuitDefinition, CircuitEdge, CircuitNode } from "@/lib/types";

const STEP_WIDTH = 240;
const STEP_HEIGHT = 92;
const END_WIDTH = 84;
const END_HEIGHT = 34;

export type CircuitInsertLocation =
  | { kind: "edge"; edgeIndex: number; edge: CircuitEdge }
  | { kind: "end"; from: string; when?: CircuitEdge["when"] }
  | { kind: "start"; to: string };

type FlowNode =
  | { key: string; kind: "step"; node: CircuitNode }
  | {
      key: string;
      kind: "end";
      sourceId: string;
      insertLocation: CircuitInsertLocation;
    };

type FlowEdge = {
  key: string;
  from: string;
  to: string;
  label: string | null;
  conditional: boolean;
  implicit: boolean;
  insertLocation: CircuitInsertLocation;
};

type PositionedNode = FlowNode & {
  x: number;
  y: number;
  width: number;
  height: number;
};

type PositionedEdge = FlowEdge & {
  points: Point[];
  labelX?: number;
  labelY?: number;
  labelWidth: number;
};

function stepLabel(node: CircuitNode): string {
  if (node.type === "agent") return node.prompt.trim().split("\n")[0] || "Agent step";
  if (node.type === "wait") {
    if (node.durationSeconds === 86_400) return "Wait 1 day";
    if (node.durationSeconds % 3_600 === 0) return `Wait ${node.durationSeconds / 3_600} hours`;
    return `Wait ${node.durationSeconds} seconds`;
  }
  return `Wait for ${node.name}`;
}

function conditionLabel(edge: CircuitEdge, hasConditionalSibling: boolean): string | null {
  if (!edge.when) return hasConditionalSibling ? "Default" : null;
  if (edge.when.path === "outcome") return String(edge.when.equals);
  const value = JSON.stringify(edge.when.equals) ?? String(edge.when.equals);
  return `${edge.when.path || "value"} = ${value}`;
}

function shortLabel(label: string): string {
  return label.length > 32 ? `${label.slice(0, 29)}...` : label;
}

export function buildCircuitGraph(definition: CircuitDefinition): {
  nodes: FlowNode[];
  edges: FlowEdge[];
} {
  const nodes: FlowNode[] = definition.nodes.map((node) => ({
    key: `step:${node.id}`,
    kind: "step",
    node,
  }));
  const edges: FlowEdge[] = [];

  for (const [index, edge] of definition.edges.entries()) {
    const siblings = definition.edges.filter((candidate) => candidate.from === edge.from);
    edges.push({
      key: `route:${index}`,
      from: `step:${edge.from}`,
      to: `step:${edge.to}`,
      label: conditionLabel(
        edge,
        siblings.some((candidate) => Boolean(candidate.when)),
      ),
      conditional: Boolean(edge.when),
      implicit: false,
      insertLocation: { kind: "edge", edgeIndex: index, edge },
    });
  }

  for (const node of definition.nodes) {
    const outgoing = definition.edges.filter((edge) => edge.from === node.id);
    const hasDefault = outgoing.some((edge) => !edge.when);
    const outcomes = node.type === "agent" ? (node.outcomes ?? []) : [];
    const routedOutcomes = new Set(
      outgoing
        .filter((edge) => edge.when?.path === "outcome")
        .map((edge) => String(edge.when?.equals)),
    );
    const missingOutcomes = hasDefault
      ? []
      : outcomes.filter((outcome) => !routedOutcomes.has(outcome));

    if (missingOutcomes.length) {
      for (const [index, outcome] of missingOutcomes.entries()) {
        const key = `end:${node.id}:${index}`;
        const insertLocation: CircuitInsertLocation = {
          kind: "end",
          from: node.id,
          when: { path: "outcome", equals: outcome },
        };
        nodes.push({ key, kind: "end", sourceId: node.id, insertLocation });
        edges.push({
          key: `implicit:${node.id}:${index}`,
          from: `step:${node.id}`,
          to: key,
          label: outcome,
          conditional: true,
          implicit: true,
          insertLocation,
        });
      }
    } else if (outgoing.length === 0 && outcomes.length === 0) {
      const key = `end:${node.id}`;
      const insertLocation: CircuitInsertLocation = { kind: "end", from: node.id };
      nodes.push({ key, kind: "end", sourceId: node.id, insertLocation });
      edges.push({
        key: `implicit:${node.id}`,
        from: `step:${node.id}`,
        to: key,
        label: null,
        conditional: false,
        implicit: true,
        insertLocation,
      });
    } else if (outcomes.length === 0 && outgoing.some((edge) => edge.when) && !hasDefault) {
      const key = `end:${node.id}:otherwise`;
      const insertLocation: CircuitInsertLocation = { kind: "end", from: node.id };
      nodes.push({ key, kind: "end", sourceId: node.id, insertLocation });
      edges.push({
        key: `implicit:${node.id}:otherwise`,
        from: `step:${node.id}`,
        to: key,
        label: "Otherwise",
        conditional: false,
        implicit: true,
        insertLocation,
      });
    }
  }

  return { nodes, edges };
}

function nodeSize(node: FlowNode): { width: number; height: number } {
  return node.kind === "step"
    ? { width: STEP_WIDTH, height: STEP_HEIGHT }
    : { width: END_WIDTH, height: END_HEIGHT };
}

function layoutCircuitGraph(definition: CircuitDefinition): {
  nodes: PositionedNode[];
  edges: PositionedEdge[];
  width: number;
  height: number;
} {
  const model = buildCircuitGraph(definition);
  const graph = new Graph<GraphLabel, NodeLabel, EdgeLabel>({ multigraph: true })
    .setGraph({
      rankdir: "TB",
      ranker: "network-simplex",
      nodesep: 44,
      edgesep: 18,
      ranksep: 78,
      marginx: 24,
      marginy: 24,
    })
    .setDefaultEdgeLabel(() => ({}));

  for (const node of model.nodes) graph.setNode(node.key, nodeSize(node));
  for (const edge of model.edges) {
    const text = edge.label ? shortLabel(edge.label) : null;
    graph.setEdge(
      edge.from,
      edge.to,
      {
        width: text ? Math.max(48, Math.min(220, text.length * 6.5 + 18)) : 0,
        height: text ? 22 : 0,
      },
      edge.key,
    );
  }

  layout(graph);
  const rawWidth = Math.ceil(graph.graph().width ?? STEP_WIDTH + 48);
  const width = Math.max(288, rawWidth);
  const height = Math.ceil(graph.graph().height ?? STEP_HEIGHT + 48);
  const offsetX = (width - rawWidth) / 2;
  const nodeByKey = new Map(model.nodes.map((node) => [node.key, node]));
  const edgeByKey = new Map(model.edges.map((edge) => [edge.key, edge]));
  const positionedNodes = graph.nodes().flatMap((key): PositionedNode[] => {
    const node = nodeByKey.get(key);
    const position = graph.node(key);
    if (!node || position.x === undefined || position.y === undefined) return [];
    const size = nodeSize(node);
    return [
      {
        ...node,
        x: position.x - size.width / 2 + offsetX,
        y: position.y - size.height / 2,
        ...size,
      },
    ];
  });
  const positionedEdges = graph.edges().flatMap((layoutEdge): PositionedEdge[] => {
    const edge = layoutEdge.name ? edgeByKey.get(layoutEdge.name) : undefined;
    const position = graph.edge(layoutEdge);
    if (!edge || !position.points) return [];
    const text = edge.label ? shortLabel(edge.label) : null;
    return [
      {
        ...edge,
        points: position.points.map((point) => ({ x: point.x + offsetX, y: point.y })),
        labelX: position.x === undefined ? undefined : position.x + offsetX,
        labelY: position.y,
        labelWidth: text ? Math.max(48, Math.min(220, text.length * 6.5 + 18)) : 0,
      },
    ];
  });

  return { nodes: positionedNodes, edges: positionedEdges, width, height };
}

function StepIcon({ node }: { node: CircuitNode }): React.JSX.Element {
  if (node.type === "agent") return <Bot size={14} />;
  if (node.type === "wait") return <Timer size={14} />;
  return <Radio size={14} />;
}

export function CircuitGraph({
  definition,
  selectedNodeId,
  onSelectNode,
  onInsert,
}: {
  definition: CircuitDefinition;
  selectedNodeId?: string | null;
  onSelectNode?: (nodeId: string) => void;
  onInsert?: (location: CircuitInsertLocation) => void;
}): React.JSX.Element {
  const graph = layoutCircuitGraph(definition);

  return (
    <div className="border-cyber-border bg-cyber-bg/25 overflow-hidden rounded-lg border">
      <div className="overflow-x-auto overscroll-x-contain">
        <div
          className="relative mx-auto"
          style={{ width: graph.width, height: graph.height }}
          aria-label={`Circuit execution graph with ${definition.nodes.length} steps`}
        >
          <svg
            viewBox={`0 0 ${graph.width} ${graph.height}`}
            className="pointer-events-none absolute inset-0 h-full w-full"
            aria-hidden="true"
          >
            <defs>
              <marker
                id="circuit-flow-arrow"
                viewBox="0 0 8 8"
                refX="7"
                refY="4"
                markerWidth="7"
                markerHeight="7"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 8 4 L 0 8 z" className="fill-cyber-muted" />
              </marker>
            </defs>
            {graph.edges.map((edge) => (
              <g key={edge.key}>
                <polyline
                  points={edge.points.map((point) => `${point.x},${point.y}`).join(" ")}
                  fill="none"
                  markerEnd="url(#circuit-flow-arrow)"
                  strokeDasharray={edge.implicit ? "4 5" : undefined}
                  className={clsx(
                    "stroke-[1.5]",
                    edge.conditional ? "stroke-cyber-cyan/55" : "stroke-cyber-border",
                  )}
                />
                {edge.label && edge.labelX !== undefined && edge.labelY !== undefined && (
                  <g>
                    <title>{edge.label}</title>
                    <rect
                      x={edge.labelX - edge.labelWidth / 2}
                      y={edge.labelY - 11}
                      width={edge.labelWidth}
                      height="22"
                      rx="5"
                      className="fill-cyber-bg stroke-cyber-border"
                    />
                    <text
                      x={edge.labelX}
                      y={edge.labelY + 3.5}
                      textAnchor="middle"
                      className="fill-cyber-cyan font-mono text-[10px]"
                    >
                      {shortLabel(edge.label)}
                    </text>
                  </g>
                )}
              </g>
            ))}
          </svg>

          {onInsert &&
            graph.edges
              .filter((edge) => !edge.implicit)
              .map((edge) => {
                const midpoint = edge.points[Math.floor(edge.points.length / 2)];
                if (!midpoint) return null;
                const x =
                  edge.labelX === undefined ? midpoint.x : edge.labelX + edge.labelWidth / 2 + 14;
                const y = edge.labelY ?? midpoint.y;
                return (
                  <button
                    key={`add:${edge.key}`}
                    type="button"
                    onClick={() => onInsert(edge.insertLocation)}
                    aria-label={`Insert step between ${edge.from.replace(/^step:/, "")} and ${edge.to.replace(/^step:/, "")}`}
                    title="Insert step on this route"
                    className="border-cyber-cyan/40 bg-cyber-bg text-cyber-cyan hover:bg-cyber-cyan hover:text-cyber-bg absolute z-10 flex h-5 w-5 items-center justify-center rounded-full border shadow transition-colors"
                    style={{ left: x - 10, top: y - 10 }}
                  >
                    <Plus size={11} />
                  </button>
                );
              })}

          {onInsert &&
            graph.nodes.flatMap((item) => {
              if (item.kind !== "step" || item.node.id !== definition.entryNodeId) {
                return [];
              }
              return [
                <button
                  key="add-before-start"
                  type="button"
                  onClick={() => onInsert({ kind: "start", to: item.node.id })}
                  aria-label={`Add step before ${item.node.id}`}
                  title="Add step before the start"
                  className="border-cyber-cyan/40 bg-cyber-bg text-cyber-cyan hover:bg-cyber-cyan hover:text-cyber-bg absolute z-10 flex h-5 w-5 items-center justify-center rounded-full border shadow transition-colors"
                  style={{ left: item.x + item.width / 2 - 10, top: item.y - 10 }}
                >
                  <Plus size={11} />
                </button>,
              ];
            })}

          {graph.nodes.map((item) => {
            if (item.kind === "end") {
              if (onInsert) {
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => onInsert(item.insertLocation)}
                    className="border-cyber-cyan/35 bg-cyber-surface/60 text-cyber-cyan hover:bg-cyber-cyan/10 absolute flex items-center justify-center gap-1 rounded-full border border-dashed text-[10px] uppercase transition-colors"
                    style={{ left: item.x, top: item.y, width: item.width, height: item.height }}
                    aria-label={`Add step after ${item.sourceId}`}
                  >
                    <Plus size={12} /> Add
                  </button>
                );
              }
              return (
                <div
                  key={item.key}
                  className="border-cyber-border bg-cyber-surface/60 text-cyber-muted absolute flex items-center justify-center gap-1.5 rounded-full border border-dashed text-[10px] uppercase"
                  style={{ left: item.x, top: item.y, width: item.width, height: item.height }}
                >
                  <CircleStop size={12} /> End
                </div>
              );
            }

            const decision = item.node.type === "agent" && Boolean(item.node.outcomes?.length);
            const entry = definition.entryNodeId === item.node.id;
            const selected = selectedNodeId === item.node.id;
            const content = (
              <>
                <div className="flex min-w-0 items-center gap-1.5">
                  <span
                    className={clsx(
                      "flex h-6 w-6 shrink-0 items-center justify-center rounded border",
                      decision
                        ? "border-amber-400/35 bg-amber-400/10 text-amber-300"
                        : "border-cyber-cyan/30 bg-cyber-cyan/10 text-cyber-cyan",
                    )}
                  >
                    <StepIcon node={item.node} />
                  </span>
                  <span className="text-cyber-muted text-[9px] tracking-wider uppercase">
                    {decision ? "Decision" : item.node.type}
                  </span>
                  {entry && (
                    <span className="bg-cyber-cyan/10 text-cyber-cyan ml-auto rounded px-1.5 py-0.5 text-[9px] tracking-wider uppercase">
                      Start
                    </span>
                  )}
                </div>
                <p className="text-cyber-text mt-2 line-clamp-2 text-xs leading-4">
                  {stepLabel(item.node)}
                </p>
                <span className="text-cyber-muted/70 mt-auto truncate font-mono text-[9px]">
                  {item.node.id}
                </span>
              </>
            );
            const className = clsx(
              "bg-cyber-surface absolute flex flex-col rounded-lg border p-3 text-left shadow-lg shadow-black/10",
              entry ? "border-cyber-cyan/70" : "border-cyber-border",
              onSelectNode && "hover:border-cyber-cyan cursor-pointer transition-colors",
              selected &&
                "ring-cyber-cyan border-cyber-cyan ring-2 ring-offset-2 ring-offset-transparent",
            );
            const style = { left: item.x, top: item.y, width: item.width, height: item.height };
            return onSelectNode ? (
              <button
                key={item.key}
                type="button"
                onClick={() => onSelectNode(item.node.id)}
                title={stepLabel(item.node)}
                className={className}
                style={style}
              >
                {content}
              </button>
            ) : (
              <div key={item.key} title={stepLabel(item.node)} className={className} style={style}>
                {content}
              </div>
            );
          })}

          <ol className="sr-only">
            {graph.edges.map((edge) => (
              <li key={edge.key}>
                {edge.from.replace(/^step:/, "")} to{" "}
                {edge.to.startsWith("end:") ? "End" : edge.to.replace(/^step:/, "")}
                {edge.label ? ` when ${edge.label}` : ""}
              </li>
            ))}
          </ol>
        </div>
      </div>
      <div className="border-cyber-border text-cyber-muted border-t px-3 py-2 text-[10px]">
        {onSelectNode
          ? "Select a step to edit it. Use + on a route or ending to insert a step."
          : "Outcomes label each branch. Dashed routes end without another step. Shared steps appear once."}
      </div>
    </div>
  );
}
